// Central content store for Bright EOR.

export const CONTACT = {
  email: "hello@brighteor.uk",
  phones: [
    { label: "Norwich office", number: "01603 976281", href: "tel:+441603976281" },
    { label: "London office", number: "020 3807 0592", href: "tel:+442038070592" },
  ],
};

export const NAV = [
  { label: "How It Works", to: "/how-it-works" },
  { label: "Services", to: "/services" },
  { label: "Managed Teams", to: "/managed-teams" },
  { label: "Pricing", to: "/pricing" },
  { label: "Roles", to: "/roles" },
  { label: "About", to: "/who-we-are" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const TRUST_POINTS = [
  { title: "India-based remote talent", body: "Access skilled professionals based in India." },
  { title: "EOR employment support", body: "Hire without setting up an Indian company." },
  { title: "Payroll coordination", body: "Keep monthly employment costs structured and clear." },
  { title: "Managed offshore support", body: "Onboarding, setup and remote team support included." },
];

export const PAIN_POINTS = [
  "No Indian company",
  "Unclear payroll setup",
  "Contract and compliance questions",
  "Remote onboarding challenges",
  "Equipment and internet concerns",
  "Communication and performance structure",
];

export const SOLUTIONS = [
  "EOR support",
  "Payroll coordination",
  "Onboarding structure",
  "Remote working checklist",
  "UK-led account support",
  "Managed offshore support",
];

// Four service levels — the client journey from one hire to a full office.
export const SERVICES = [
  {
    slug: "single-hire",
    icon: "user",
    level: "Service 1",
    title: "Single-Hire EOR",
    headline: "Hire one dedicated India-based professional.",
    tagline: "One employee, dedicated to you — employed and supported by us.",
    body: "For businesses that need one employee, or already have a candidate in India they want to employ.",
    control: "The employee works directly for your business. You manage their daily priorities and role-specific performance. Bright EOR manages the employment, payroll, equipment, HR and local support structure.",
    includes: [
      "Role consultation",
      "Candidate sourcing & screening where required",
      "Curated shortlist — you interview & select",
      "Employment contract & India payroll",
      "Statutory employment administration",
      "Laptop, headset & UK phone number where required",
      "Internet & mobile-data support",
      "IT onboarding & offboarding",
      "HR admin, attendance & employee check-ins",
      "Equipment replacement & replacement support (agreed terms)",
      "Single monthly invoice",
    ],
  },
  {
    slug: "team-pod",
    icon: "users",
    level: "Service 2",
    title: "Managed Team Pod",
    headline: "Build a dedicated team around one part of your business.",
    tagline: "Two to four connected employees performing a defined function.",
    body: "For companies that need a small, coordinated team — for example two candidate resourcers and a compliance administrator, or two customer-service executives and an order administrator.",
    control: "Everything in Single-Hire EOR, plus multi-role planning, coordinated recruitment and team-level reporting. You still choose every person and control the work.",
    includes: [
      "Everything in Single-Hire EOR",
      "Multi-role workforce planning",
      "Team responsibility mapping",
      "Coordinated recruitment campaign",
      "Shared onboarding plan",
      "Team communication structure",
      "Basic process documentation",
      "Attendance & productivity reporting",
      "Weekly team summary & monthly review",
      "Local escalation support",
      "Expansion planning & optional team leader",
    ],
  },
  {
    slug: "offshore-function",
    icon: "network",
    level: "Service 3",
    title: "Managed Offshore Function",
    headline: "Launch a complete offshore department.",
    tagline: "A five-person-plus recruitment, finance, customer-service, sales, marketing, ops or admin function.",
    body: "For businesses building a complete offshore department, designed, structured and launched around your business.",
    control: "Choose a Client-Led Function (you run daily priorities; we run the local operation) or a Fully Managed Function (we supply a dedicated India-based team leader who handles allocation, supervision, KPIs, QA, coaching, reporting and escalations). Fully managed functions are bespoke and subject to a detailed scope.",
    includes: [
      "Offshore-function design & org structure",
      "Role scorecards & team-leader recruitment",
      "Process mapping & standard operating procedures",
      "Performance measures & QA structure",
      "Weekly management reporting & monthly reviews",
      "Recruitment forecasting",
      "Performance-improvement support",
      "Team training coordination",
      "Continuity & scale-up planning",
    ],
    options: [
      {
        name: "A · Client-Led Function",
        body: "You manage daily priorities and output. Bright EOR manages the employment, local operation, team structure and support environment.",
      },
      {
        name: "B · Fully Managed Function",
        body: "Bright EOR supplies a dedicated India-based team leader or operations manager who handles daily allocation, supervision, KPI tracking, QA, coaching, reporting and escalations. Bespoke and scoped in detail.",
      },
    ],
  },
  {
    slug: "india-office",
    icon: "building",
    level: "Service 4",
    title: "Dedicated India Office",
    headline: "Build a dedicated office or delivery centre in India.",
    tagline: "A physical office or delivery centre, run through Bright EOR — no Indian entity required.",
    body: "For businesses that need a dedicated on-ground presence. We set it up and run it through our company, with the option to establish your own Indian entity later.",
    control: "Enquiry-only and priced per project. Build-operate-transfer discussions where appropriate.",
    includes: [
      "Managed office through Bright EOR — no Indian entity required",
      "Workspace, recruitment & employment",
      "Payroll, IT & secure internet",
      "Backup connectivity",
      "HR & local management",
      "On-ground escalation support",
      "Option to establish your own Indian entity later",
      "Build-operate-transfer discussions where appropriate",
    ],
  },
];

export const HOW_IT_WORKS = [
  { step: "01", title: "Tell us who you need", body: "You share the role, salary range, working hours, experience level and support required." },
  { step: "02", title: "We source or you bring the candidate", body: "You can bring your own person, or we source and screen India-based candidates and share you a curated shortlist to interview." },
  { step: "03", title: "You interview and select", body: "You pick who joins your team. Not the other way round — we don't tell you who to hire." },
  { step: "04", title: "We handle setup, you get to work", body: "Bright EOR runs employment, payroll, IT onboarding, equipment and support structure. Your team member reports directly into you." },
];

// Researched cold markets — priority target industries for first outreach.
export const TARGET_MARKETS = [
  {
    icon: "calc",
    industry: "Accountancy, bookkeeping & payroll firms",
    body: "Free your qualified UK team from admin-heavy grind and give clients faster turnaround at healthier margins.",
    roles: [
      "Bookkeepers",
      "Payroll assistants",
      "Management accounts prep",
      "VAT & bank reconciliation",
      "Xero / QuickBooks specialists",
      "Client onboarding admin",
    ],
  },
  {
    icon: "banknote",
    industry: "Mortgage, finance & insurance brokers",
    body: "Move case admin, fact-finds and renewals off the desks of your advisers so they can focus on closing.",
    roles: [
      "Mortgage case administrators",
      "Insurance renewal admin",
      "Fact-find preparation",
      "Underwriting support",
      "Client servicing executives",
      "Appointment setters",
    ],
  },
  {
    icon: "scale",
    industry: "Small law firms",
    body: "Support your fee-earners with reliable back-office capacity — without paying paralegal-level UK salaries.",
    roles: [
      "Legal admin",
      "Paralegal support",
      "Conveyancing admin",
      "File-opening & AML",
      "Billing & matter admin",
      "Diary & document management",
    ],
  },
  {
    icon: "briefcase",
    industry: "Specialist recruitment agencies",
    body: "Whether you're a boutique agency or a 360 desk operator, a strong India-based resourcing layer moves the needle.",
    roles: [
      "Candidate resourcers",
      "CV formatting & job board posting",
      "LinkedIn outreach",
      "Interview coordination",
      "Compliance & onboarding admin",
      "CRM administrators",
    ],
  },
  {
    icon: "globe",
    industry: "Small digital, SEO & web agencies",
    body: "Scale delivery without scaling your UK payroll — get technical hands-on-keyboard support for a fraction of local cost.",
    roles: [
      "SEO analysts & auditors",
      "Content writers",
      "Link outreach specialists",
      "WordPress support & QA",
      "PPC / paid social support",
      "Reporting & CRM admin",
    ],
  },
];

// Additional industries we also support — shown as compact cards under the priority 5.
export const ADDITIONAL_MARKETS = [
  {
    icon: "heart",
    industry: "Care providers & regulated services",
    body: "Back-office, recruitment coordination and compliance admin for children's homes, home care and regulated providers.",
  },
  {
    icon: "shopping",
    industry: "E-commerce & consumer brands",
    body: "Customer service, order admin, product listing, review management and returns support.",
  },
  {
    icon: "code",
    industry: "SaaS & technology startups",
    body: "Customer success, tier-1 support, QA, product research and CRM administration.",
  },
  {
    icon: "home",
    industry: "Property, lettings & estate agencies",
    body: "Property admin, listings management, tenant coordination, viewings scheduling and CRM upkeep.",
  },
  {
    icon: "megaphone",
    industry: "Marketing, PR & creative agencies",
    body: "Copywriting, media outreach, campaign coordination, reporting and social scheduling.",
  },
  {
    icon: "chart",
    industry: "Wealth management & IFA firms",
    body: "Client servicing admin, fact-find prep, compliance documentation and platform admin.",
  },
];

// Alt persona callouts for the smaller "Also great for" strip.
export const ALSO_FOR = [
  "Founders who already have a candidate in India",
  "UK companies building an offshore delivery team",
  "Businesses that want a dedicated India office",
];

export const ROLES = [
  "Customer service staff",
  "Sales support",
  "Business development executives",
  "Recruitment resourcers",
  "Candidate resourcers",
  "Virtual assistants",
  "Admin support",
  "CRM administrators",
  "Marketing assistants",
  "Website support",
  "SEO support",
  "Finance/admin support",
  "Operations coordinators",
  "Data/admin roles",
  "Client support executives",
  "Appointment setters",
];

// Everything included with EVERY Bright EOR hire.
export const INCLUDED_WITH_HIRE = [
  "Laptop and headset provided",
  "Dedicated UK phone number",
  "AI-enabled call recording & conversation summaries",
  "Internet & mobile data reimbursement",
  "Employment contracts & onboarding",
  "HR administration",
  "Payroll management",
  "Local employment & statutory compliance",
  "IT onboarding & offboarding",
  "Performance check-ins",
  "Leadership & operational support (as required)",
  "Employee engagement & wellbeing (as required)",
  "Equipment replacement on hardware failure",
  "Background verification (where applicable)",
  "Replacement support (where applicable)",
  "Single monthly invoice",
];

export const ADD_ON = {
  name: "Productivity monitoring add-on",
  price: "£20",
  unit: "per employee / month",
  body: "Optional productivity software giving you visibility of working hours, application usage, activity levels and productivity insights through a secure online dashboard.",
};

export const PRICING = [
  {
    name: "Dedicated India-based professional",
    price: "From £895",
    unit: "/month",
    subtitle: "One all-inclusive monthly price. Final quote depends on the role, experience, working hours and the employee you select.",
    highlight: true,
    includes: [
      "Recruitment, employment & India payroll",
      "Laptop, headset & UK number where required",
      "IT onboarding, HR & attendance support",
      "Equipment & replacement cover (agreed terms)",
      "Local support & employee escalations",
      "One clear monthly invoice — no itemised breakdown",
    ],
  },
  {
    name: "One-time setup",
    price: "From £295",
    unit: "one-time",
    subtitle: "Depends on whether you bring a candidate or we recruit and launch for you.",
    highlight: false,
    includes: [
      "Bring your own candidate — from £295",
      "Bright EOR recruitment & launch — from £495",
      "Managed Team Pod — from £750",
      "Managed Offshore Function — bespoke after scoping",
    ],
  },
];

export const PRICING_NOTE =
  "One clear monthly price based on the role, experience required, equipment, employment costs and managed support included. Final pricing depends on the role, experience level, working hours and employee selected. Your quote is provided as one all-inclusive monthly figure.";

// Indicative monthly ranges — examples, not guaranteed fixed prices.
export const ROLE_RANGES = [
  { label: "Junior support roles", range: "typically £895–£1,100 / month" },
  { label: "Experienced operational roles", range: "typically £1,100–£1,400 / month" },
  { label: "Specialist & management roles", range: "quoted individually" },
];

export const SETUP_FEES = [
  { label: "Bring your own candidate", price: "From £295", unit: "one-time" },
  { label: "Bright EOR recruitment & launch", price: "From £495", unit: "one-time" },
  { label: "Managed Team Pod", price: "From £750", unit: "one-time" },
  { label: "Managed Offshore Function", price: "Bespoke", unit: "after team scoping" },
];

export const MANAGED_TEAM_PRICING = {
  pod: [
    "Each employee receives a separate all-inclusive monthly price",
    "Additional team coordination from £250 / month",
    "Enhanced team reporting & performance support from £400 / month",
  ],
  func: [
    "Per-employee monthly pricing",
    "Function-management fee",
    "Dedicated team-lead cost where required",
    "Launch fee following discovery",
  ],
  note: "Managed functions are designed and priced around team size, roles, management requirements, systems and performance responsibilities.",
};

// The client journey — start with one and scale.
export const PROGRESSION = [
  { stage: "Stage 1", title: "One dedicated employee", body: "Start with a single India-based professional, employed and supported by us." },
  { stage: "Stage 2", title: "A connected team pod", body: "Add two to four people performing a defined function around your business." },
  { stage: "Stage 3", title: "A complete offshore function", body: "Scale to a five-person-plus department with structure and reporting." },
  { stage: "Stage 4", title: "A dedicated India office", body: "Establish a physical office or delivery centre — no Indian entity required." },
];

// Outsourcing vs Bright EOR.
export const COMPARISON = {
  traditional: [
    "Provider assigns the people",
    "Staff may work across several accounts",
    "Limited influence over selection",
    "Outsourced team identity",
    "Provider controls the delivery model",
  ],
  brighteor: [
    "We recruit for your exact requirements",
    "You interview and select",
    "Employees are dedicated to your business",
    "They work as part of your company",
    "You control priorities and processes",
    "Bright EOR manages the local employment structure",
  ],
};

// Example teams for the Managed Teams page + homepage cards.
export const EXAMPLE_TEAMS = [
  {
    icon: "briefcase",
    name: "Recruitment Delivery Team",
    roles: ["Candidate resourcers", "Compliance administrators", "Interview coordinators", "CRM administrators", "Team leader"],
  },
  {
    icon: "calc",
    name: "Finance Operations Team",
    roles: ["Bookkeeping assistants", "Payroll administrators", "Client onboarding administrators", "Mortgage / insurance case administrators"],
  },
  {
    icon: "headset",
    name: "Customer Operations Team",
    roles: ["Customer-service executives", "Order administrators", "CRM support", "Returns & review support", "Team leader"],
  },
  {
    icon: "trend",
    name: "Growth Support Team",
    roles: ["Appointment setters", "Prospect researchers", "CRM administrators", "Marketing assistants"],
  },
];

// Managed-team build journey (separate from the single-hire 4-step).
export const MANAGED_JOURNEY = [
  { step: "01", title: "Function discovery", body: "We map the function, the outcomes you need and how it fits your business." },
  { step: "02", title: "Team & role design", body: "We design the roles, responsibilities and structure of the team." },
  { step: "03", title: "Cost & launch proposal", body: "You receive an all-inclusive proposal covering roles, pricing and launch." },
  { step: "04", title: "Recruitment & selection", body: "We recruit for your exact requirements. You interview and select every person." },
  { step: "05", title: "Systems & process setup", body: "We set up systems access, tools and basic process documentation." },
  { step: "06", title: "Structured onboarding", body: "A shared onboarding plan gets the team productive quickly and consistently." },
  { step: "07", title: "Reporting & review", body: "Attendance, productivity and team summaries, with regular reviews." },
  { step: "08", title: "Team expansion", body: "Start with one, add people as the model proves itself. Scale on your terms." },
];

export const WHY = [
  { title: "UK-led, India-experienced", body: "Built by a UK founder who has spent years living and working in India — we understand both sides." },
  { title: "Built on recruitment experience", body: "Bright EOR sits under Brightridge Partners with deep UK recruitment DNA — briefing, screening, salary and onboarding." },
  { title: "More than payroll", body: "We add a full managed layer around each hire — onboarding, IT, comms, wellbeing and performance." },
  { title: "Practical offshore setup", body: "We handle the details that make offshore work: laptops, UK phone lines, internet, power backup, CRM access." },
  { title: "India-focused", body: "We are not trying to cover every country — that keeps the service specific, practical and properly supported." },
  { title: "Founder-led support", body: "You are not dealing with a faceless platform. You get direct, founder-led support around making the hire work." },
];

export const INDIA_ADVANTAGE = [
  "Large English-speaking talent pool",
  "Strong experience across sales, admin, tech and support roles",
  "UK time zone overlap",
  "Cost-effective hiring compared to equivalent UK roles",
  "Strong remote working culture",
  "Ideal for back-office and growth support teams",
];

export const ECOSYSTEM = [
  {
    name: "Brightridge Recruitment",
    url: "https://www.brightridge.uk",
    body: "Specialist recruitment for UK children's homes and regulated care providers.",
  },
  {
    name: "BrightLabs",
    url: "https://www.brightlabs.live",
    body: "Websites, SEO, automation and AI-powered growth systems.",
  },
  {
    name: "Bright EOR",
    url: null,
    body: "India EOR support, offshore hiring and managed remote team support.",
    current: true,
  },
];

export const FAQS = [
  { q: "What is an EOR?", a: "An Employer of Record helps a business employ someone in another country without setting up its own local entity." },
  { q: "Does Bright EOR only support India?", a: "Yes. Bright EOR is focused on helping UK businesses hire and manage India-based remote talent. This keeps the service focused, practical and properly supported." },
  { q: "Do I need to open an Indian company?", a: "No. Bright EOR helps you access India-based talent without needing to set up an Indian company yourself. If you'd rather have your own entity, we can help set that up too." },
  { q: "Can I bring my own candidate?", a: "Yes. If you already have someone in India you want to hire, Bright EOR handles employment, onboarding and the ongoing support structure." },
  { q: "Can you help us find candidates?", a: "Yes. We handle role briefing, sourcing and screening, then share you a curated shortlist. You interview and choose — unlike outsourcing agencies who hand you whoever they've got." },
  { q: "Who manages the employee day to day?", a: "You do. Your team member reports into your business as if they were in-house. Bright EOR runs employment, payroll, IT, wellbeing and performance structure in the background." },
  { q: "What's included in the monthly fee?", a: "Every hire includes a laptop and headset, dedicated UK phone number, AI-enabled call recording and conversation summaries, internet/mobile data reimbursement, employment contracts, HR admin, payroll, IT onboarding and offboarding, performance check-ins, wellbeing support, equipment replacement, background checks, and a single monthly invoice." },
  { q: "Is there an optional productivity monitoring tool?", a: "Yes — an optional add-on at £20 per employee / month gives you a secure dashboard with working hours, application usage, activity levels and productivity insights." },
  { q: "Can we set up a physical office or dedicated team in India?", a: "Yes. We can set up and run an office through Bright EOR (no entity required on your side), or help you incorporate and stand up your own Indian entity. Pricing is scoped per project." },
  { q: "Is VAT reclaimable?", a: "Bright EOR is UK-based. Where VAT is charged on our service fees, it is normally reclaimable by UK VAT-registered businesses via your standard VAT return. Employee salaries and India-side statutory costs pass through separately. We'll walk you through the exact invoicing treatment on quote." },
  { q: "How much does it cost?", a: "Dedicated India-based professionals start from £895/month, provided as one all-inclusive monthly figure. One-time setup starts from £295 if you bring your own candidate, or £495 if we recruit and launch for you. Final pricing depends on the role, experience level, working hours and employee selected." },
  { q: "Is this cheaper than hiring in the UK?", a: "Hiring in India can be more cost-effective than an equivalent UK role, but Bright EOR is not a 'cheap labour' service and we don't promise guaranteed savings. The focus is skilled, dedicated talent, proper structure and managed support." },
  { q: "Can we start with one employee and add more later?", a: "Yes — most clients do. Start with a single dedicated hire, then scale into a team pod, a full offshore function or a dedicated office as the model proves itself." },
  { q: "What is the difference between EOR and a managed team?", a: "Single-Hire EOR employs and supports one dedicated person who works directly for you. A managed team adds multi-role planning, coordinated recruitment, team reporting and — if you want it — a dedicated team leader who runs day-to-day delivery." },
  { q: "Are employees dedicated exclusively to us?", a: "Yes. Every employee works exclusively for your business as part of your team. They are never shared across other clients." },
  { q: "Who chooses the employees?", a: "You do. We recruit for your exact requirements and share a curated shortlist — you interview and select every person." },
  { q: "Who manages daily work?", a: "You manage daily priorities and role-specific performance. Bright EOR manages the employment, payroll, equipment, HR and local support structure. If you choose a fully managed function, we also supply a team leader who handles daily allocation and supervision." },
  { q: "Can Bright EOR provide a team leader?", a: "Yes — an optional team leader is available for team pods, and a dedicated India-based team leader or operations manager is included in a fully managed offshore function." },
  { q: "Can you build an entire department?", a: "Yes. A Managed Offshore Function builds a complete five-person-plus department — recruitment, finance, customer service, sales support, marketing, operations or administration — with structure, SOPs, QA and reporting." },
  { q: "Can our India team use our existing systems?", a: "Yes. Your team works inside your systems — CRM, email, tools and processes — as part of your business. We coordinate access and IT onboarding." },
  { q: "Can we establish an office later?", a: "Yes. You can begin with remote hires and move to a dedicated India office when it makes sense, with the option to establish your own Indian entity or discuss build-operate-transfer." },
  { q: "How quickly can a team launch?", a: "It depends on the roles and team size. Single hires move quickly; larger functions follow a discovery and design stage. We'll give you a realistic timeline in your proposal." },
  { q: "How is managed-team pricing calculated?", a: "Each employee has a separate all-inclusive monthly price. Team pods add optional coordination and reporting fees; full functions are designed and priced around team size, roles, management requirements, systems and performance responsibilities." },
  { q: "What happens when a team member leaves?", a: "Replacement support is available under agreed terms — we re-recruit and re-onboard so your operation stays continuous." },
  { q: "Can we transfer the operation into our own Indian company later?", a: "Yes. Where appropriate we can discuss build-operate-transfer and help you establish your own Indian entity, moving the operation across when you're ready." },
];

export const INTEREST_OPTIONS = [
  "One India-based employee",
  "Two-to-four-person team",
  "Five-person-plus function",
  "Dedicated India office",
  "I already have a candidate",
  "I am not sure yet",
];

// Kept for backwards compatibility with any older references.
export const SUPPORT_OPTIONS = INTEREST_OPTIONS;

// Co-founders for the About / Who We Are page.
export const PEOPLE = [
  {
    name: "Richard Rozalewicz",
    role: "Co-Founder · Bright EOR",
    subrole: "Founder · Brightridge Partners",
    initials: "RR",
    image: "/team/richard.png",
    bio: [
      "Founder of Brightridge Partners — the group behind Brightridge Recruitment, BrightLabs and Bright EOR. Co-Founder of Bright EOR.",
      "15+ years across recruitment, operations, learning & development and business building — with hands-on experience running and leading teams across both the UK and India. Splits time between markets as the business requires.",
      "Brings the UK recruitment DNA, business-owner perspective and group-level operational experience that underpin every Bright EOR hire.",
    ],
    links: [
      { label: "LinkedIn", href: "https://www.linkedin.com/in/richard-rozalewicz/" },
      { label: "Brightridge", href: "https://www.brightridge.uk" },
      { label: "BrightLabs", href: "https://www.brightlabs.live" },
    ],
  },
  {
    name: "Kevin C.",
    role: "Co-Founder · Bright EOR",
    subrole: "Co-Founder · Brightridge Recruitment",
    initials: "KC",
    image: "/team/kevin.png",
    bio: [
      "Co-Founder of Bright EOR and Co-Founder of Brightridge Recruitment. Operates across both the UK and India as the business requires.",
      "Deep recruitment and delivery-operations experience — with a track record of building and leading teams that make offshore hiring work end-to-end.",
      "Focused on candidate quality, on-ground execution and the operational structure that Bright EOR is known for.",
    ],
    links: [
      { label: "LinkedIn", href: "https://www.linkedin.com/in/kevin-c-124589138/" },
      { label: "Brightridge", href: "https://www.brightridge.uk" },
    ],
  },
];

export const RECRUITMENT_PROCESS = [
  { title: "You brief us on the role", body: "Salary, seniority, hours, must-haves, nice-to-haves." },
  { title: "We source and screen", body: "We handle the outreach, screening calls and role-fit assessment." },
  { title: "You get a curated shortlist", body: "Only candidates we'd interview ourselves. Usually 3–5 per role." },
  { title: "You interview and select", body: "You choose the person. Not the other way round." },
  { title: "We handle everything else", body: "Offer, onboarding, IT, employment, payroll and ongoing management." },
];
